import React from 'react'
import PropTypes from 'prop-types'
import { Link, useLocation } from 'react-router-dom'
import { Trans } from '@lingui/macro'
import { Typography } from '@telavita-core/react-design-kit'

import Row from './../../components/Row'
import MenuDashboardItem from './../../components/MenuDashboardItem'
import PermissionValidator from '../PermissionValidator'

import * as permissions from './../../settings/_permissionSettings'
import * as route from '../../settings/_routesSettings'
import { PSYCHOLOGIST } from '../../settings/_profileSettings'
import { useProfile } from 'hooks'

import GeneralView from 'components/CoreIcons/Icons/GeneralView'
import House from 'components/CoreIcons/Icons/House'
import Agenda from 'components/CoreIcons/Icons/Agenda'
import MyPatients from 'components/CoreIcons/Icons/MyPatients'
import ArrowClock from 'components/CoreIcons/Icons/ArrowClock'
import ListAlt from 'components/CoreIcons/Icons/ListAlt'
import Group from 'components/CoreIcons/Icons/Group'
import ProfessionalFull from 'components/CoreIcons/Icons/ProfessionalFull'
import GroupPlanBuilding from 'components/CoreIcons/Icons/GroupPlanBuilding'
import CreditCard from 'components/CoreIcons/Icons/CreditCard'
import PersonCheck from 'components/CoreIcons/Icons/PersonCheck'
import Graphs from 'components/CoreIcons/Icons/Graphs'
import Plans from 'components/CoreIcons/Icons/Plans'
import Hourglass from 'components/CoreIcons/Icons/Hourglass'
import ChatBubble from 'components/CoreIcons/Icons/ChatBubble'
import Graphics from 'components/CoreIcons/Icons/Graphics'

import { ReactComponent as CommunityIcon } from 'static/svg/icon-community.svg'
import { ReactComponent as SettingsIcon} from 'static/svg/icon-settings.svg'
import { useProfessionalMedicalRequests } from 'hooks/useProfessionalMedicalRequests'

const MenuDashboardContainer = () => {
  const profile = useProfile()
  const location = useLocation()

  const pathName = location.pathname

  const fetchValidities = profile.isProfessional

  const { pagination } = useProfessionalMedicalRequests(fetchValidities)

  const numberOfValidities = pagination?.total
  const hasValidities = numberOfValidities > 0

  return (
    <Row>
      <PermissionValidator permission={permissions.ADMIN_GENERAL_VIEW}>
        <Link to={route.DASHBOARD_GENERAL_VIEW}>
          <MenuDashboardItem
            title={<Trans>Visão Geral</Trans>}
            isSelected={pathName === route.DASHBOARD_GENERAL_VIEW}
            icon={
              <GeneralView 
                fill='#1F35B5'
                height='24px'
                width='24px'
                viewBox='0 0 28 22'
              />
            }
            id={`${permissions.ADMIN_GENERAL_VIEW}`}
          />
        </Link>
      </PermissionValidator>

      {
        profile.isProfessional &&
        <Link to={route.DASHBOARD_HOME}>
          <MenuDashboardItem
            title={<Trans>Início</Trans>}
            id="menu-dashboard-item-home"
            isSelected={pathName === route.DASHBOARD_HOME}
            icon={
              <House 
                fill='#1F35B5'
                height='24px'
                width='24px'
                viewBox='0 0 24 24'
              />
            }
          />
        </Link>
      }

      {
        profile.isPatient &&
          <Link to={route.DASHBOARD_HOME_PATIENT}>
            <MenuDashboardItem
              title={<Trans>Início</Trans>}
              id="menu-dashboard-item-home"
              isSelected={pathName === route.DASHBOARD_HOME_PATIENT}
              icon={
                <House 
                  fill='#1F35B5'
                  height='24px'
                  width='24px'
                  viewBox='0 0 24 24'
                />
              }
            />
          </Link>
      }

      <PermissionValidator permission={permissions.USER_VIEW_MYAPPOINTMENTS}>
        <Link to={route.DASHBOARD_APPOINTMENTS}>
          <MenuDashboardItem
            title={<Trans>Minhas consultas</Trans>}
            isSelected={pathName === route.DASHBOARD_APPOINTMENTS}
            icon={
              <Agenda 
                fill='#1F35B5'
                height='24px'
                width='24px'
                viewBox='0 0 24 24'
              />
            }
            id={`${permissions.USER_VIEW_MYAPPOINTMENTS}`}
          />
        </Link>
      </PermissionValidator>

      {
        profile.isPatient &&
        <Link to={route.DASHBOARD_PATIENT_PLANS}>
          <MenuDashboardItem
            title={<Trans>Meus convênios</Trans>}
            id="menu-dashboard-item-home"
            isSelected={pathName === route.DASHBOARD_PATIENT_PLANS}
            icon={
              <GroupPlanBuilding 
                fill='#1F35B5'
                height='24px'
                width='24px'
                viewBox='0 0 24 24'
              />
            }
          />
        </Link>
      }
      {profile.isPatient &&
          <Link to={route.DASHBOARD_PATIENT_SETTINGS}>
            <MenuDashboardItem
              title='Configurações'
              id="menu-dashboard-item-settings"
              isSelected={pathName === route.DASHBOARD_PATIENT_SETTINGS}
              icon={<SettingsIcon fill='#1F35B5' />}
            />
          </Link>}

      <PermissionValidator permission={permissions.USER_VIEW_AGENDA}>
        <Link to={route.DASHBOARD_AGENDA}>
          <MenuDashboardItem
            title={<Trans>Minha agenda</Trans>}
            isSelected={pathName === route.DASHBOARD_AGENDA}
            icon={
              <Agenda 
                fill='#1F35B5'
                height='24px'
                width='24px'
                viewBox='0 0 24 24'
              />
            }
            id={`${permissions.USER_VIEW_AGENDA}`}
          />
        </Link>
      </PermissionValidator>

      <PermissionValidator permission={permissions.USER_VIEW_PATIENTS}>
        <Link to={route.DASHBOARD_PATIENTS}>
          <MenuDashboardItem
            title={<Trans>Meus pacientes</Trans>}
            isSelected={pathName === route.DASHBOARD_PATIENTS}
            icon={
              <MyPatients 
                fill='#1F35B5'
                height='24px'
                width='24px'
              />
            }
            id={`${permissions.USER_VIEW_PATIENTS}`}
          />
        </Link>
      </PermissionValidator>

      {
        profile.isPsychologist &&
        <Link to={route.DASHBOARD_RESERVATIONS_PROFESSIONAL}>
          <MenuDashboardItem
            title={<Trans>Reservas</Trans>}
            isSelected={pathName === route.DASHBOARD_RESERVATIONS_PROFESSIONAL}
            id="menu-dashboard-item-home"
            icon={
              <ArrowClock 
                fill='#1F35B5'
                height='24px'
                width='24px'
              />
            }
          />
        </Link>
      }

      <PermissionValidator permission={permissions.ADMIN_VIEW_APPOINTMENTS}>
        <Link to={route.DASHBOARD_ADMIN_SCHEDULES}>
          <MenuDashboardItem
            title={<Trans>Lista de Consultas</Trans>}
            isSelected={pathName === route.DASHBOARD_ADMIN_SCHEDULES}
            icon={
              <ListAlt 
                fill='#1F35B5'
                height='24px'
                width='24px'
                viewBox='0 0 24 24'
              />
            }
            id={`${permissions.ADMIN_VIEW_APPOINTMENTS}`}
          />
        </Link>
      </PermissionValidator>

      <PermissionValidator permission={permissions.ADMIN_VIEW_PATIENTS}>
        <Link to={route.DASHBOARD_CLIENT_LIST}>
          <MenuDashboardItem
            title={<Trans>Pacientes</Trans>}
            isSelected={pathName === route.DASHBOARD_CLIENT_LIST}
            icon={
              <Group 
                fill='#1F35B5'
                height='20px'
                width='24px'
                viewBox='3 0 20 20'
              />
            }
            id={`${permissions.ADMIN_VIEW_PATIENTS}`}
          />
        </Link>
      </PermissionValidator>

      <PermissionValidator permission={permissions.ADMIN_VIEW_PROFESSIONALS}>
        <Link to={route.DASHBOARD_PROFESSIONAL_LIST(PSYCHOLOGIST)}>
          <MenuDashboardItem
            title={<Trans>Profissionais</Trans>}
            isSelected={pathName === route.DASHBOARD_PROFESSIONAL_LIST(PSYCHOLOGIST)}
            icon={
              <ProfessionalFull 
                fill='#1F35B5'
                height='20px'
                width='24px'
                viewBox='3 1 20 20'
              />
            }
            id={`${permissions.ADMIN_VIEW_PROFESSIONALS}`}
          />
        </Link>
      </PermissionValidator>

      {
        profile.isAdmin &&
        <Link to={route.DASHBOARD_PLAN_GROUP_LIST_VIEW}>
          <MenuDashboardItem
            title={<Trans>Convênios</Trans>}
            icon={
              <GroupPlanBuilding 
                fill='#1F35B5'
                height='24px'
                width='24px'
                viewBox='0 0 24 24'
              />
            }
            id='plan-groups'
          />
        </Link>
      }

      <PermissionValidator permission={permissions.ADMIN_VIEW_PAYMENT}>
        <Link to={route.DASHBOARD_PAYMENT_LIST}>
          <MenuDashboardItem
            title={<Trans>Pedidos</Trans>}
            isSelected={pathName === route.DASHBOARD_PAYMENT_LIST}
            icon={
              <CreditCard 
                fill='#1F35B5'
                height='24px'
                width='24px'
                viewBox='1 1 23 2'
              />
            }
            id={`${permissions.ADMIN_VIEW_PAYMENT}`}
          />
        </Link>
      </PermissionValidator>

      <PermissionValidator permission={permissions.ADMIN_VIEW_PERMISSIONS}>
        <Link to={route.DASHBOARD_PERMISSIONS}>
          <MenuDashboardItem
            title={<Trans>Permissões</Trans>}
            icon={
              <PersonCheck 
                fill='#1F35B5'
                height='24px'
                width='24px'
                viewBox='0 0 24 24'
              />
            }
            id={`${permissions.ADMIN_VIEW_PERMISSIONS}`}
          />
        </Link>
      </PermissionValidator>

      <PermissionValidator permission={permissions.ADMIN_VIEW_REPORTS}>
        <Link to={route.DASHBORAD_REPORTS}>
          <MenuDashboardItem
            title={<Trans>Relatório</Trans>}
            icon={
              <Graphs 
                fill='#1F35B5'
                height='20px'
                width='24px'
                viewBox='3 2 19 19'
              />
            }
            id={`${permissions.ADMIN_VIEW_REPORTS}`}
          />
        </Link>
      </PermissionValidator>

      <PermissionValidator permission={permissions.USER_VIEW_PLANS}>
        <Link to={route.DASHBOARD_PLANS}>
          <MenuDashboardItem
            title={<Trans>Convênios</Trans>}
            icon={
              <Plans 
                fill='#1F35B5'
                height='20px'
                width='24px'
                viewBox='3 2 19 19'
              />
            }
            id={`${permissions.USER_VIEW_PLANS}`}
          />
        </Link>
      </PermissionValidator>
      
      {/* <PermissionValidator permission={permissions.USER_VIEW_MEDICAL_REQUEST}>
        <Link to={route.DASHBORAD_MEDICAL_REQUESTS}>
          <MenuDashboardItem
            title={<Trans>Solicitações</Trans>}
            icon={
              <Plans 
                fill='#1F35B5'
                height='20px'
                width='24px'
                viewBox='3 2 19 19'
              />
            }
            id={`${permissions.USER_VIEW_MEDICAL_REQUEST}`}
          />
        </Link>
      </PermissionValidator> */}

      <PermissionValidator permission={permissions.ADMIN_VIEW_MEDICAL_REQUEST}>
        <Link to={route.DASHBORAD_ADMIN_MEDICAL_REQUESTS}>
          <MenuDashboardItem
            title={<Trans>Solicitações</Trans>}
            icon={
              <Hourglass 
                fill='#1F35B5'
                height='20px'
                width='24px'
                viewBox='0 2 26 19.8'
              />
            }
            id={`${permissions.ADMIN_VIEW_MEDICAL_REQUEST}`}
          />
        </Link>
      </PermissionValidator>

      {
        profile.isProfessional &&
        <Link to={route.DASHBOARD_REVIEWS_PROFESSIONAL}>
          <MenuDashboardItem
            title={<Trans>Depoimentos</Trans>}
            isSelected={pathName === route.DASHBOARD_REVIEWS_PROFESSIONAL}
            id="reviews"
            icon={
              <ChatBubble 
                fill='#1F35B5'
                height='24px'
                width='24px'
                viewBox='0 0 24 24'
              />
            }
          />
        </Link>
      }

      {
        profile.isPsychologist &&
        <Link to={route.DASHBOARD_GRAPHICS_PROFESSIONAL}>
          <MenuDashboardItem
            title="Gráficos"
            id="graphics"
            isSelected={pathName === route.DASHBOARD_GRAPHICS_PROFESSIONAL}
            icon={
              <Graphics 
                fill='#1F35B5'
                height='24px'
                width='24px'
                viewBox='0 0 24 24'
              />
            }
          />
        </Link>
      }

      <PermissionValidator permission={permissions.USER_VIEW_MEDICAL_REQUEST}>
        <Link to={route.DASHBORAD_MEDICAL_REQUESTS}>
          <MenuDashboardItem
            badge={
              hasValidities && (
                <Typography
                  variant='content2'
                  customClassName='MenuDashboardContainer__ValiditiesNotification'
                  inlineStyles={{
                    color: '#fff',
                    padding: '2px 7px',
                  }}
                >
                  {numberOfValidities}
                </Typography>
              )
            }
            title={<Trans>Solicitações</Trans>}
            icon={
              <Hourglass 
                fill='#1F35B5'
                height='20px'
                width='24px'
                viewBox='0 2 26 19.8'
              />
            }
            id={`${permissions.USER_VIEW_MEDICAL_REQUEST}`}
          />
        </Link>
      </PermissionValidator>

      {
        profile.isProfessional &&
        <Link to={{ pathname: process.env.REACT_APP_TELAVITA_COMMUNITY_URL }} target='_blank'>
          <MenuDashboardItem
            title="Comunidade"
            id="community"
            icon={<CommunityIcon />}
          />
        </Link>
      }
    </Row>
  )
}

MenuDashboardContainer.propTypes = {
  userPermissions: PropTypes.array,
  userProfile: PropTypes.string,
}

export default MenuDashboardContainer
